/**
 * Типы консультантской ветки — зеркало backend-контракта
 * (`backend/src/ikolhoz/api/v1/consultant_clients.py`).
 */

/** Чем занимается компания. Решает, что человек видит в меню. */
export type BusinessKind = 'agro' | 'consultant' | 'hybrid';

/**
 * Состояние клиента у нас — три человеческих состояния (флоу консультанта,
 * шаг 4), четырьмя техническими значениями:
 * - `external_only` / `card_only` — «нет в системе»: хозяйство не заводили
 *   (карточка CRM при этом может существовать — визиты, звонки);
 * - `farm_ready` — «заведён · полей нет»: имя и отдел заданы, кабинет
 *   создастся под первое поле;
 * - `in_system` — «полей: N»: есть поля, кабинет живой.
 */
export type ClientState = 'in_system' | 'farm_ready' | 'card_only' | 'external_only';

/** Хозяйство заведено? Ровно этим гасятся действия в строке и в карточке. */
export function hasFarm(state: ClientState): boolean {
  return state === 'farm_ready' || state === 'in_system';
}

export interface ConsultantClient {
  /** Bitrix company ID — якорь записи. */
  external_id: string;
  title: string | null;
  inn: string | null;
  region: string | null;
  city: string | null;
  district: string | null;
  assigned_by_id: number | null;
  assigned_by_name: string | null;
  client_status: string | null;
  area_ha: number | null;
  is_priority: boolean | null;
  crop_direction: string | null;
  farm_size_category: string | null;
  segment_crops: boolean | null;
  segment_gardens: boolean | null;
  segment_other: boolean | null;
  date_modify: string | null;
  state: ClientState;
  crm_company_id: string | null;
  linked_tenant_id: string | null;
  /** Имя хозяйства клиента, как его завели (предзаполняется из CRM). */
  farm_name: string | null;
  /** Отдел/филиал (кластер) консультанта, который ведёт это хозяйство. */
  cluster_id: string | null;
  cluster_name: string | null;
  /** Путь по отделам («КАВОП › Отдел Рощупкиной»), считает сервер. */
  department_path: string | null;
  /**
   * Полей в кабинете клиента. Различает однофамильцев с одного взгляда:
   * «Зелюка Наталья Николаевна КФХ — полей: 1» против «Зелюка Александр
   * Григорьевич — полей: 0» (кейс founder 18.08). У незаведённого — 0.
   */
  fields_count: number;
}

export interface ClientTreeNode {
  id: string;
  title: string;
  /** Клиентов в узле ВМЕСТЕ с потомками. */
  count: number;
  children: ClientTreeNode[];
}

/** Что фронт просит у сервера: страница + фильтры. Всё считается на сервере. */
export interface ClientsQuery {
  page?: number;
  perPage?: number;
  q?: string;
  node?: string;
  onlyInSystem?: boolean;
  refresh?: boolean;
}

export interface ConsultantClientsResponse {
  items: ConsultantClient[];
  /** Сколько попало под фильтры (счётчик над таблицей). */
  total: number;
  /** Весь пул клиентов — «Все клиенты» в дереве. */
  pool_total: number;
  page: number;
  per_page: number;
  pages: number;
  tree: { nodes: ClientTreeNode[]; unassigned: number };
  /** Синхронизация ИСТОЧНИКА — от неё считается «актуально N минут назад». */
  synced_at: string | null;
  /** Наш последний успешный поход за данными. Не путать с synced_at. */
  fetched_at: string;
  reconciled_at: string | null;
  stale: boolean;
  age_s: number | null;
}

export interface ClientWindow {
  tenant_id: string;
  tenant_name: string | null;
  /**
   * Срок окна. У новых входов всегда `null` — окно бессрочно и закрывается
   * только явным выходом. Поле осталось ради окон, выданных до этой смены:
   * их гранты дописаны с `valid_until` и доживают свой час.
   */
  expires_at: string | null;
}

export interface ConsultantProfile {
  business_kind: BusinessKind;
  /** Не null — мы стоим в чужом окне, и об этом говорит индикатор в шапке. */
  client_window: ClientWindow | null;
  /**
   * Подключён ли внешний список клиентов ИМЕННО этому тенанту. Источник
   * глобальный (один токен — одна база), поэтому «тип компании = консультант»
   * ещё не значит, что список ему положен: адресат задан явно на сервере.
   * Меню не должно звать в раздел, который тут же ответит 404 — предложенная
   * и ломающаяся кнопка хуже отсутствующей.
   */
  clients_source_connected: boolean;
}

export interface ClientFieldsSummary {
  has_window: boolean;
  tenant_id?: string;
  fields_count: number;
  area_ha: number;
  last_scene_date: string | null;
}

export interface OnboardResult {
  crm_company_id: string;
  /**
   * Кабинет клиента. `null` сразу после «Завести»: кабинет создаётся лениво,
   * под первое поле (модель D), а «Завести» лишь помечает карточку рабочей.
   */
  linked_tenant_id: string | null;
  /** Карточка создана ЭТИМ вызовом (а не найдена по якорю). */
  created: boolean;
}
